import { Component, Event, EventEmitter, h, JSX, Prop, State } from '@stencil/core';
import { DateUtils } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { TimelineBubble } from '../../global/models/Timeline';

@Component({
  tag: 'concourse-timeline',
  styleUrl: 'concourse-timeline.css',
  shadow: true,
})
export class ConcourseTimeline {
  @Prop() bubbles: TimelineBubble[] = [];
  @Prop() activeIndex: number = 0;
  @Prop() dueDate: Date | null = null;
  @Prop() hideCreateBubble: boolean = false;

  @State() today: Date = DateUtils.currentDate;
  @State() barWidth: number = 0;

  @Event() bubbleClick: EventEmitter<number[]>;
  @Event() barClick: EventEmitter<number>;
  @Event() dueDateClick: EventEmitter<number>;

  private timelineBarEl: HTMLElement;
  private resizeObserver: ResizeObserver;

  componentDidLoad() {
    this.setupResizeObserver();
  }

  private setupResizeObserver() {
    if (!this.timelineBarEl) return;
    this.resizeObserver = new ResizeObserver(entries => {
      this.barWidth = entries[0]?.contentRect.width ?? 0;
    });
    this.resizeObserver.observe(this.timelineBarEl);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  roundTime(date: Date): number {
    return Math.round(date.getTime() / DateUtils.MILLIS_PER_HOUR) * DateUtils.MILLIS_PER_HOUR;
  }

  render(): JSX.Element {
    if (!this.barWidth || this.barWidth < 40 || !this.bubbles || this.bubbles.length === 0) {
      return (
        <div class="timeline-container">
          <div class="timeline-bar" ref={el => this.setTimelineBarRef(el)}></div>
        </div>
      );
    }
    const startTime = this.roundTime(this.bubbles[0].doneDate || this.bubbles[0].dueDate);
    const lastState = this.bubbles[this.bubbles.length - 1];
    const lastStateDate = lastState.doneDate ?? lastState.dueDate;
    const endTime = this.dueDate ? Math.max(this.roundTime(lastStateDate), this.roundTime(this.dueDate)) : this.roundTime(lastStateDate);
    let prevBarX = 0;
    let prevBubbleX = 0;
    let bubbleGroup: number[] = [];
    return (
      <div class="timeline-container">
        {/* ttimeline markers come first so they are at the back of the stack */}
        {this.roundTime(this.today) >= startTime && this.roundTime(this.today) <= endTime && (
          <div class="timeline-marker" style={{ left: `${this.getDateXPos(startTime, endTime, this.today)}px` }}>
            <small class="rounded-bg">today {this.today.toISOString().substring(5, 10)}</small>
            <div class="vertical-bar" />
          </div>
        )}
        {this.dueDate && (
          <div
            class="timeline-marker"
            style={{ left: `${this.getDateXPos(startTime, endTime, this.dueDate)}px`, cursor: 'pointer' }}
            onClick={() => this.dueDateClick.emit(this.bubbles.length - 1)}
          >
            <small class="rounded-bg">due {this.dueDate.toISOString().substring(5, 10)}</small>
            <div class="vertical-bar" />
          </div>
        )}
        <div class="timeline-bar" ref={el => this.setTimelineBarRef(el)}>
          {this.bubbles.slice(1).map((state, index) => {
            const currentIdx = this.activeIndex - 1;
            const dueDatePx = this.getDateXPos(startTime, endTime, state.dueDate);
            const doneDatePx = state.doneDate ? this.getDateXPos(startTime, endTime, state.doneDate) : null;
            const barEndX = doneDatePx || dueDatePx; // Bubbles in the future have no 'Done Date'
            const barStartX = prevBarX;
            prevBarX = barEndX;
            if (barEndX - barStartX < 10) {
              // bubbles close together, no need for a bar
              return <div />;
            }
            return (
              <div
                class={`bar ${!state.doneDate ? 'clickable' : ''}`}
                style={{ left: `${barStartX}px`, width: `${barEndX - barStartX}px` }}
                onClick={() => (!state.doneDate ? this.barClick.emit(index) : console.log('Not clickable'))}
              >
                <div class={index < currentIdx ? 'complete' : index == currentIdx ? 'current' : 'future'} style={{ width: `${dueDatePx - barStartX}px` }} />
                {doneDatePx && doneDatePx > dueDatePx && <div class="overdue" style={{ left: `${dueDatePx - barStartX}px`, width: `${doneDatePx - dueDatePx}px` }} />}
              </div>
            );
          })}
          {this.dueDate && (
            <div
              class="bar clickable"
              style={{ left: `${prevBarX}px`, width: `${this.getDateXPos(startTime, endTime, this.dueDate) - prevBarX}px` }}
              onClick={() => this.barClick.emit(this.bubbles.length - 1)}
            >
              <div class="future" style={{ width: `100%` }} />
            </div>
          )}
        </div>
        {this.bubbles.map((state, index) => {
          const date = state.doneDate || state.dueDate;
          let posX = this.getDateXPos(startTime, endTime, date);
          const next = index < this.bubbles.length - 1 ? this.bubbles[index + 1] : null;
          const closeToPrev = index > 0 && posX < prevBubbleX + 5; // if too close, push it right
          if (closeToPrev) {
            bubbleGroup.push(index);
          } else {
            bubbleGroup = [index];
          }
          const nextPosX = next ? this.getDateXPos(startTime, endTime, next.doneDate || next.dueDate) : null;
          const closeToNext = nextPosX && nextPosX && nextPosX < posX + 50; // if too close, push it right
          prevBubbleX = Math.max(posX, prevBubbleX + 5); // give space if they are too close
          const isOverdue = state.doneDate && state.doneDate.getTime() > state.dueDate.getTime();
          const isCurrent = index === this.activeIndex;
          const bubbleClass = index > this.activeIndex ? 'future' : isOverdue ? 'overdue' : isCurrent ? 'current' : 'complete';
          const bubbleIcon = PeopleService.getGroupIcon(state.people);
          const bubbleGroupCopy = [...bubbleGroup]; // Create a copy to avoid mutation issues
          const initials = state.people.length == 1 && !state.people[0].image?.data ? PeopleService.getInitials(state.people[0]) : '';
          if (this.hideCreateBubble && index == 0) return <div />;
          return (
            <div class="timeline-bubble" style={{ left: `${prevBubbleX}px` }} onClick={() => this.bubbleClick.emit(bubbleGroupCopy)}>
              <div class="circle-stack">
                <div class={`circle ${bubbleClass} ${closeToPrev ? 'shadow-left' : ''}`} style={{ backgroundImage: `url(${bubbleIcon})` }}>
                  {initials}
                </div>
                {index < this.activeIndex && <ion-icon name="checkmark-circle" class="check-complete"></ion-icon>}
              </div>
              {(isCurrent || !closeToNext) && (
                <div class={`label ${isCurrent ? '' : 'ion-hide-sm-down'}`}>
                  <div>{state.label}</div>
                  <small>{date.toISOString().substring(5, 10)}</small>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  private setTimelineBarRef(el: HTMLElement) {
    if (!el || el === this.timelineBarEl) return;
    this.timelineBarEl = el;
    // If componentDidLoad already ran, set up the observer now (happens when switching render branches)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.setupResizeObserver();
  }

  getDateXPos(startTime: number, endTime: number, date: Date): number {
    return ((date.getTime() - startTime) / (endTime - startTime)) * this.barWidth;
  }
}
